import type { FC } from "react"
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
} from "@chakra-ui/react"

import ProposalShareMenuItem from "proposal/components/ctas/ProposalShareMenuItem"
import ProposalBlockExplorerURLButton from "proposal/components/ctas/ProposalBlockExplorerURLButton"
import { ROUTES } from "common/constants/routes"
import type { Governor, Proposal } from "query/graphql"
import { ProposalStatus, useProposalExecutedCtaQuery } from "query/graphql"
import { getGovernorProposalTxHash } from "proposal/helpers/status"
import { Bolt } from "ui/components/icons/font-awesome/Bolt"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import { useOrganization } from "organization/providers/OrganizationProvider"
import ShareToFarcasterButton from "common/components/ShareToFarcasterButton"
import ShareToLensButton from "common/components/ShareToLensButton"
import { FeatureFlag, useGovernanceContext } from "governance/context"

type ProposalExecutedCTAProps = {
  proposalId: string
  slug: string
}
const ProposalExecutedCTA: FC<ProposalExecutedCTAProps> = ({
  proposalId,
  slug,
}) => {
  const { data, isLoading } = useProposalExecutedCtaQuery({
    input: { id: proposalId },
  })

  const proposal = data?.proposal

  const { isFeatureFlagOn } = useGovernanceContext()

  const isL1ExecutionEnabled = isFeatureFlagOn(FeatureFlag.L2Execution)

  const showL2Execution =
    isL1ExecutionEnabled && proposal?.status === ProposalStatus.Executed

  if (isLoading) return null

  if (!proposal) return null

  return (
    <Stack
      align="center"
      color="gray.600"
      direction="row"
      justify={{ base: "center", lg: "right" }}
      spacing={4}
      width="100%"
    >
      <Stack
        align="center"
        direction="row"
        width={{ base: "100%", sm: "auto" }}
      >
        <Icon as={Bolt} fill="gray.600" />
        <Text textStyle="body.bold.lg">
          {showL2Execution ? "Pending L1 execution" : "Proposal executed"}
        </Text>
      </Stack>
      <OptionsList
        governance={proposal.governor as Governor}
        proposal={proposal as Proposal}
        slug={slug}
      />
    </Stack>
  )
}

type OptionsListProps = {
  governance: Governor
  proposal: Proposal
  slug: string
}
const OptionsList: FC<OptionsListProps> = ({ governance, proposal, slug }) => {
  const { title } = useDescriptionException({
    governanceId: governance.id,
    proposal,
  })

  const { organization } = useOrganization()

  const txHash = getGovernorProposalTxHash(proposal)

  return (
    <Flex>
      <Menu>
        <MenuButton
          as={Button}
          bgColor="white"
          borderColor="gray.300"
          borderWidth="1px"
          px={3}
        >
          <Icon as={EllipsisVertical} fill="gray.600" />
        </MenuButton>
        <MenuList>
          {organization && proposal?.onchainId ? (
            <ProposalShareMenuItem
              url={ROUTES.governance.proposal.id(
                slug,
                proposal.onchainId,
                organization.governorIds.length && proposal.governor
                  ? proposal.governor.id
                  : "",
              )}
            />
          ) : null}

          {organization && title ? (
            <>
              <MenuItem>
                <ShareToFarcasterButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
              <MenuItem>
                <ShareToLensButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
            </>
          ) : null}

          {txHash ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={governance.chainId}
                txHash={txHash}
              />
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ProposalExecutedCTA
